import React, { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { BeneficiariesTable } from './components/BeneficiariesTable/BeneficiariesTable';
import { BeneficiaryType } from '../../../../PersonalInfo/api/useTrustsQuery';
import { Label } from '../../../../../CommonComponents/OPL/Label/Label';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { SingleDatePicker } from '../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { InsuranceData } from '../../../useInsurance';
import { FormFields } from '../../../components/UpsertInsuranceModal/UpsertInsuranceModal.meta';

const EMPTY_BENEFICIARY = {
  Name: '',
  Percentage: '',
  Id: null,
} as any;

interface Props {
  accountId: string;
  data: InsuranceData;
}

export const Beneficiaries = ({ data }: Props) => {
  const { control, watch } = useFormContext<FormFields>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'AccountBeneficiaries.Beneficiaries',
    keyName: 'key',
  });

  const accountOwner = watch('AccountOwner');
  const trustOwnerOptions = data.accountOptions.OwnerOptions.Trusts ?? [];
  const isReadOnly = !!trustOwnerOptions.find(option => option === accountOwner);

  const fieldArray = watch('AccountBeneficiaries.Beneficiaries');

  const [beneficiariesOptions, setBeneficiariesOptions] = useState<
    { label: string; value: string | null; isVisible: boolean }[]
  >(
    data.accountOptions.BeneficiariesOptions.map(b => ({
      label: b.Name,
      value: b.Id,
      isVisible: !fieldArray.find(({ Id }) => Id === b.Id),
    })),
  );

  const beneficiaries = fields.map((field, index) => {
    return {
      ...field,
      ...fieldArray[index],
    };
  });

  return (
    <>
      <BeneficiariesTable
        type={BeneficiaryType.Primary}
        beneficiaries={beneficiaries}
        options={beneficiariesOptions}
        onAppend={() => append({ Type: BeneficiaryType.Primary, ...EMPTY_BENEFICIARY })}
        onRemove={index => remove(index)}
        onUpdateOptions={setBeneficiariesOptions}
        width="auto"
        mx={-24}
        my="24"
        isReadOnly={isReadOnly}
      />

      <BeneficiariesTable
        type={BeneficiaryType.Contingent}
        beneficiaries={beneficiaries}
        options={beneficiariesOptions}
        onAppend={() => append({ Type: BeneficiaryType.Contingent, ...EMPTY_BENEFICIARY })}
        onRemove={index => remove(index)}
        onUpdateOptions={setBeneficiariesOptions}
        width="auto"
        mx={-24}
        isReadOnly={isReadOnly}
      />

      {!isReadOnly && (
        <>
          <Label css="display: block;" mt="24" mb="4">
            When were these instructions last updated?
          </Label>
          <Controller
            control={control}
            name="AccountBeneficiaries.LastBeneficiaryUpdate"
            render={({ field: { value, onChange } }) => (
              <SingleDatePicker
                date={value === null ? new Date() : new Date(value)}
                onChange={onChange}
                maxDate={new Date()}
                minDate={new Date(1900, 0)}
              />
            )}
          />
        </>
      )}

      <Text fontSize="small" color="neutral.500" mt="24">
        This is for financial planning informational purposes only. Any information entered here
        will not be updated at your financial institution. To update beneficiary information with
        your financial institution, please contact your advisor.
      </Text>
    </>
  );
};
